import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import { showSuccess } from '../components/GlobalSnackbar';
import { formatFen } from '../utils/money';

const EXPENSE_TYPES = [
  { value: 'transport', label: '运输费' },
  { value: 'meal', label: '餐费' },
  { value: 'accommodation', label: '住宿费' },
  { value: 'tool', label: '工具费' },
  { value: 'rental', label: '租赁费' },
  { value: 'communication', label: '通讯费' },
  { value: 'entertainment', label: '招待费' },
  { value: 'other', label: '其他' },
];

export default function ExpenseList() {
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [newExpense, setNewExpense] = useState({
    projectId: '',
    date: new Date().toISOString().slice(0, 10),
    expenseType: 'transport',
    amount: '',
    description: '',
    handler: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [expensesRes, projectsRes] = await Promise.all([
        api.get('/api/expenses'),
        api.get('/api/projects?pageSize=500'),
      ]);
      setExpenses(expensesRes.data.data || expensesRes.data);
      setProjects(projectsRes.data.data || projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.error || error.message || '加载杂费记录失败');
    } finally {
      setLoading(false);
    }
  };

  const submitLock = useRef(false); // 写锁：与 QuotationDetail.writeLock 同款防连点

  const handleCreate = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const expense = {
          id: Date.now().toString(),
          ...newExpense,
          amount: Math.round(parseFloat(newExpense.amount || 0) * 100),
          paymentStatus: 0,
        };
        await api.post('/api/expenses', expense);
        setOpenDialog(false);
        resetForm();
        showSuccess('杂费已保存');
        fetchData();
      } catch (error) {
        console.error('Error creating expense:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleEditStart = (item) => {
    setEditingItem(item);
    setNewExpense({
      projectId: item.projectId || '',
      date: item.date || '',
      expenseType: item.expenseType || 'transport',
      amount: item.amount ? String(item.amount / 100) : '',
      description: item.description || '',
      handler: item.handler || '',
      notes: item.notes || '',
    });
    setOpenDialog(true);
  };

  const handleEditSave = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const data = {
          ...newExpense,
          amount: Math.round(parseFloat(newExpense.amount || 0) * 100),
        };
        await api.put(`/api/expenses/${editingItem.id}`, data);
        setOpenDialog(false);
        setEditingItem(null);
        resetForm();
        showSuccess('杂费已更新');
        fetchData();
      } catch (error) {
        console.error('Error updating expense:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/expenses/${confirmDialog.id}`);
      setConfirmDialog({ open: false, id: null });
      fetchData();
      showSuccess('杂费记录已删除');
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const resetForm = () => {
    setNewExpense({
      projectId: '',
      date: new Date().toISOString().slice(0, 10),
      expenseType: 'transport',
      amount: '',
      description: '',
      handler: '',
      notes: '',
    });
  };


  const getExpenseTypeText = (type) => {
    return EXPENSE_TYPES.find(t => t.value === type)?.label || type;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>杂费管理</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchData}>重新加载</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">杂费管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingItem(null); resetForm(); setOpenDialog(true); }}
        >
          新增杂费
        </Button>
      </Box>

      {expenses.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              暂无杂费记录
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>日期</TableCell>
                <TableCell>项目</TableCell>
                <TableCell>费用类型</TableCell>
                <TableCell>说明</TableCell>
                <TableCell>经办人</TableCell>
                <TableCell align="right">金额</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell>{expense.date}</TableCell>
                  <TableCell>{expense.projectName || '-'}</TableCell>
                  <TableCell>{getExpenseTypeText(expense.expenseType)}</TableCell>
                  <TableCell>{expense.description || '-'}</TableCell>
                  <TableCell>{expense.handler || '-'}</TableCell>
                  <TableCell align="right">{formatFen(expense.amount)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditStart(expense)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(expense.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setEditingItem(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? '编辑杂费' : '新增杂费'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="项目"
            fullWidth
            variant="outlined"
            value={newExpense.projectId}
            onChange={(e) => setNewExpense({ ...newExpense, projectId: e.target.value })}
            sx={{ mb: 2 }}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="日期"
            fullWidth
            variant="outlined"
            type="date"
            value={newExpense.date}
            onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="费用类型"
            fullWidth
            variant="outlined"
            value={newExpense.expenseType}
            onChange={(e) => setNewExpense({ ...newExpense, expenseType: e.target.value })}
            sx={{ mb: 2 }}
          >
            {EXPENSE_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="金额（元）"
            fullWidth
            variant="outlined"
            type="number"
            value={newExpense.amount}
            onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="说明"
            fullWidth
            variant="outlined"
            value={newExpense.description}
            onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="经办人"
            fullWidth
            variant="outlined"
            value={newExpense.handler}
            onChange={(e) => setNewExpense({ ...newExpense, handler: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="备注"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={newExpense.notes}
            onChange={(e) => setNewExpense({ ...newExpense, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setEditingItem(null); }}>取消</Button>
          <Button onClick={editingItem ? handleEditSave : handleCreate} variant="contained" disabled={!newExpense.projectId || !(parseFloat(newExpense.amount) > 0)}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="确认删除"
        message="确定要删除这条杂费记录吗？"
      />
    </Box>
  );
}
